import type { FormEvent } from 'react';
import { Controller } from 'react-hook-form';
import { Button, ConfirmActionModal, Input, Label } from '@shared/components';
import { MotorplanChannelFields } from './MotorplanChannelFields';
import type { MotorplanPaymentFormState } from '../hooks/useMotorplanPaymentForm';
import {
    PAY_OFF_CONFIRM_TITLE,
    PAY_OFF_ONLY_EXPLANATION,
    payOffConfirmMessage,
    payOffHint,
    SEND_BLOCKED_REASON,
} from '../utils/motorplanPaymentCopy';

interface Props {
    form: MotorplanPaymentFormState;
    id: string;
    leftToPay: string;
    planStaysActive: boolean;
    autoFocusAmount?: boolean;
}

const AMOUNT_ID = 'motorplan-payment-amount';
const AMOUNT_HINT_ID = 'motorplan-payment-amount-hint';
const AMOUNT_ERROR_ID = 'motorplan-payment-amount-error';
const SEND_BLOCKED_ID = 'motorplan-payment-send-blocked';

export function MotorplanPaymentForm({ form, id, leftToPay, planStaysActive, autoFocusAmount = false }: Props) {
    const { control, submit, isSubmitting, canSubmit, formError, paysOff, payOffOnly, payOffOffered, confirming, confirmPayOff, cancelConfirm } = form;
    const guardedSubmit = (event: FormEvent<HTMLFormElement>) => {
        if (isSubmitting) {
            event.preventDefault();
            return;
        }
        void submit(event);
    };
    const sendLabel = paysOff ? 'Send pay-off link' : 'Send payment link';
    const showHint = leftToPay !== '' && payOffOffered && !payOffOnly;
    const blocked = !canSubmit && !isSubmitting;

    return (
        <form id={id} onSubmit={guardedSubmit} className="space-y-4" noValidate>
            {payOffOnly && <p className="text-sm text-mp-foreground">{PAY_OFF_ONLY_EXPLANATION}</p>}
            <div>
                <Label htmlFor={AMOUNT_ID} required>
                    Amount to pay
                </Label>
                {showHint && (
                    <p id={AMOUNT_HINT_ID} className="text-sm text-mp-foreground">
                        {payOffHint(leftToPay)}
                    </p>
                )}
                <Controller
                    name="amountGross"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            ref={field.ref}
                            id={AMOUNT_ID}
                            type="text"
                            inputMode="decimal"
                            autoFocus={autoFocusAmount}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={!!error}
                            hint={error?.message}
                            hintId={AMOUNT_ERROR_ID}
                            aria-invalid={!!error}
                            aria-describedby={error ? AMOUNT_ERROR_ID : showHint ? AMOUNT_HINT_ID : undefined}
                        />
                    )}
                />
            </div>

            <MotorplanChannelFields control={control} />

            {formError && (
                <p role="alert" className="text-sm text-mp-error">
                    {formError}
                </p>
            )}

            <Button
                type="submit"
                size="sm"
                className="w-full"
                disabled={isSubmitting}
                ariaDisabled={blocked}
                ariaDescribedBy={blocked ? SEND_BLOCKED_ID : undefined}
            >
                {sendLabel}
            </Button>
            {blocked && (
                <p id={SEND_BLOCKED_ID} className="text-sm text-mp-muted">
                    {SEND_BLOCKED_REASON}
                </p>
            )}

            <ConfirmActionModal
                isOpen={confirming}
                title={PAY_OFF_CONFIRM_TITLE}
                message={payOffConfirmMessage(planStaysActive)}
                confirmLabel="Send pay-off link"
                confirmVariant="primary"
                isPending={isSubmitting}
                onConfirm={() => void confirmPayOff()}
                onClose={cancelConfirm}
            />
        </form>
    );
}
